import React from "react";
import {
  getNativeCanvasToolTitle,
  NATIVE_CANVAS_COMMANDS,
  NATIVE_CANVAS_TOOLS,
} from "./nativeCanvasToolbar.tools";

const ACTIVE_BUTTON_CLASS = "border-primary bg-info-surface text-primary";
const AI_ATTENTION_CLASS = "canvas-ai-attention-ring";
const BUTTON_CLASS =
  "relative flex h-10 w-10 shrink-0 items-center justify-center rounded-md border-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-35";
const DIVIDER_CLASS = "mx-1 h-7 w-px bg-border";
const INACTIVE_BUTTON_CLASS =
  "border-transparent bg-transparent text-text hover:bg-surface-muted";
const SHORTCUT_CLASS =
  "absolute bottom-1 right-1 text-[10px] font-semibold leading-none text-text-muted";
const TOOLBAR_CLASS =
  "pointer-events-auto absolute left-1/2 top-4 z-30 flex max-w-[calc(100%-24px)] -translate-x-1/2 items-center gap-1 overflow-x-auto rounded-xl border border-border bg-surface/95 p-2 shadow-2xl backdrop-blur";
const ZOOM_LABEL_CLASS =
  "min-w-[54px] select-none text-center text-xs font-semibold text-text-muted";

const getToolButtonClassName = ({ className, isActive }) =>
  [
    BUTTON_CLASS,
    isActive ? ACTIVE_BUTTON_CLASS : INACTIVE_BUTTON_CLASS,
    className,
  ]
    .filter(Boolean)
    .join(" ");
const handleToolbarButtonMouseDown = (event) => event.preventDefault();
const shouldShowShortcut = (shortcut) => Boolean(shortcut && shortcut.length <= 2);

const ToolbarButton = ({
  className = "",
  command,
  disabled = false,
  isActive = false,
  onClick,
}) => {
  const { Icon, iconClassName = "", label, shortcut } = command;
  const title = getNativeCanvasToolTitle(command);

  return (
    <button
      aria-label={label}
      aria-pressed={isActive}
      className={getToolButtonClassName({ className, isActive })}
      data-testid={`netpad-canvas-tool-${command.id}`}
      disabled={disabled}
      onClick={onClick}
      onMouseDown={handleToolbarButtonMouseDown}
      title={title}
      type="button"
    >
      <Icon className={["h-5 w-5", iconClassName].join(" ").trim()} fontSize="small" />
      {shouldShowShortcut(shortcut) && (
        <span className={SHORTCUT_CLASS}>{shortcut}</span>
      )}
    </button>
  );
};

const ToolButton = ({ isActive, onToolChange, tool }) => (
  <ToolbarButton
    command={tool}
    isActive={isActive}
    onClick={() => onToolChange(tool.id)}
  />
);

const CanvasToolControls = (toolbarProps) => (
  <>
    <ToolbarButton
      command={NATIVE_CANVAS_COMMANDS.STICKY}
      isActive={toolbarProps.isToolLocked}
      onClick={toolbarProps.onToggleToolLock}
    />
    <span className={DIVIDER_CLASS} />
    {NATIVE_CANVAS_TOOLS.map((tool) => (
      <ToolButton
        isActive={toolbarProps.selectedTool === tool.id}
        key={tool.id}
        onToolChange={toolbarProps.onToolChange}
        tool={tool}
      />
    ))}
    <ToolbarButton
      className={AI_ATTENTION_CLASS}
      command={NATIVE_CANVAS_COMMANDS.AI}
      isActive={toolbarProps.isAiPanelOpen}
      onClick={toolbarProps.onAiPanelToggle}
    />
    <span className={DIVIDER_CLASS} />
  </>
);

const HistoryControls = (toolbarProps) => (
  <>
    <ToolbarButton
      command={NATIVE_CANVAS_COMMANDS.UNDO}
      disabled={!toolbarProps.canUndo}
      onClick={toolbarProps.onUndo}
    />
    <ToolbarButton
      command={NATIVE_CANVAS_COMMANDS.REDO}
      disabled={!toolbarProps.canRedo}
      onClick={toolbarProps.onRedo}
    />
  </>
);

const ZoomControls = (toolbarProps) => (
  <>
    <ToolbarButton
      command={NATIVE_CANVAS_COMMANDS.ZOOM_OUT}
      onClick={toolbarProps.onZoomOut}
    />
    <span aria-label="Canvas zoom" className={ZOOM_LABEL_CLASS}>
      {toolbarProps.zoomLabel}
    </span>
    <ToolbarButton
      command={NATIVE_CANVAS_COMMANDS.ZOOM_IN}
      onClick={toolbarProps.onZoomIn}
    />
  </>
);

/**
 * @param {object} props
 * @returns {JSX.Element}
 */
const NativeCanvasToolbar = (props) => (
  <div aria-label="Canvas tools" className={TOOLBAR_CLASS} role="toolbar">
    <CanvasToolControls {...props} />
    <HistoryControls {...props} />
    <ZoomControls {...props} />
  </div>
);

export default NativeCanvasToolbar;
